import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { paymentsAPI, referralsAPI } from "../lib/api";
import { 
    Shield, 
    FileText, 
    TrendingUp, 
    CheckCircle2, 
    ArrowRight,
    Upload,
    Sparkles,
    Scale,
    Clock,
    BadgeCheck,
    Gift
} from "lucide-react";

const REF_STORAGE_KEY = "creditfix_ref";

const LandingPage = () => {
    const [isHovered, setIsHovered] = useState(null);
    const [pricing, setPricing] = useState({ price: null, formatted: '…' });
    const [referralInfo, setReferralInfo] = useState(null);
    const [searchParams] = useSearchParams();

    useEffect(() => {
        loadPricing();
    }, []);

    useEffect(() => {
        const fromUrl = searchParams.get("ref");
        const stored = localStorage.getItem(REF_STORAGE_KEY);
        const candidate = (fromUrl || stored || "").trim().toUpperCase();
        if (!candidate) return;
        let active = true;
        (async () => {
            try {
                const r = await referralsAPI.info(candidate);
                if (!active) return;
                if (r.data?.discount_percent > 0) {
                    setReferralInfo(r.data);
                    localStorage.setItem(REF_STORAGE_KEY, candidate);
                }
            } catch {
                if (active) {
                    localStorage.removeItem(REF_STORAGE_KEY);
                    setReferralInfo(null);
                }
            }
        })();
        return () => { active = false; };
    }, [searchParams]);

    const loadPricing = async () => {
        try {
            const response = await paymentsAPI.getPricing();
            setPricing(response.data);
        } catch (error) {
            // Non-critical — fall back to display defaults
        }
    };

    const features = [
        {
            icon: Upload,
            title: "Upload Your Reports",
            description: "Simply upload your credit reports from any bureau. Our AI handles the rest."
        },
        {
            icon: Sparkles,
            title: "AI-Powered Analysis",
            description: "Advanced AI identifies errors, violations, and opportunities using FCRA guidelines."
        },
        {
            icon: FileText,
            title: "Professional Letters",
            description: "Get legally-compliant dispute letters ready to send to bureaus and creditors."
        },
        {
            icon: TrendingUp,
            title: "Action Plan",
            description: "Follow a step-by-step plan tailored to your specific credit situation."
        }
    ];

    const benefits = [
        "FCRA & CFPB Compliant Letters",
        "Identifies All Three Bureau Errors",
        "Debt Validation Templates",
        "Goodwill Letter Templates",
        "30-Day Dispute Timeline",
        "Legal Citation References"
    ];

    return (
        <div className="min-h-screen bg-background relative overflow-hidden">
            {/* Navigation */}
            <nav className="nav-glass fixed top-0 left-0 right-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold text-foreground">CreditFix AI</span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <Link to="/login">
                            <Button variant="ghost" className="font-medium" data-testid="login-nav-btn">
                                Sign In
                            </Button>
                        </Link>
                        <Link to="/register">
                            <Button className="rounded-full px-6 btn-press" data-testid="get-started-nav-btn">
                                Get Started
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4 md:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div 
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="space-y-8"
                        >
                            {referralInfo?.discount_percent > 0 ? (
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.96 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.45 }}
                                    className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-accent text-accent-foreground shadow-sm"
                                    data-testid="landing-referral-banner"
                                >
                                    <Gift className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                        <span className="font-semibold">{referralInfo.referrer_first_name}</span> sent you{" "}
                                        <span className="font-bold">{referralInfo.discount_percent}% off</span>
                                    </span>
                                </motion.div>
                            ) : (
                                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 text-accent-foreground border border-accent/20">
                                    <Scale className="h-4 w-4" />
                                    <span className="text-sm font-medium">FCRA & CFPB Compliant</span>
                                </div>
                            )}

                            {referralInfo?.discount_percent > 0 ? (
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight">
                                    Claim Your <span className="block text-primary">{referralInfo.discount_percent}% Off</span>
                                </h1>
                            ) : (
                                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight">
                                    Repair Your Credit
                                    <span className="block text-primary">With AI Precision</span>
                                </h1>
                            )}
                            
                            <p className="text-base md:text-lg text-muted-foreground max-w-lg">
                                {referralInfo?.discount_percent > 0 ? (
                                    <>
                                        Your <span className="font-semibold text-foreground">{referralInfo.discount_percent}% discount</span> is reserved. Create your account, upload your reports, and the AI will draft FCRA-compliant dispute letters — with the discount auto-applied at checkout.
                                    </>
                                ) : (
                                    <>Upload your credit reports and let our AI identify errors, generate dispute letters, and create a personalized action plan—all backed by federal consumer protection laws.</>
                                )}
                            </p>
                            
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link to="/register">
                                    <Button 
                                        size="lg" 
                                        className="rounded-full px-8 text-base btn-press group"
                                        data-testid="hero-get-started-btn"
                                    >
                                        {referralInfo?.discount_percent > 0
                                            ? `Claim ${referralInfo.discount_percent}% off`
                                            : "Start Free Analysis"}
                                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                    </Button>
                                </Link>
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <CheckCircle2 className="h-4 w-4 text-accent" />
                                    <span>Free preview • Pay only for full access</span>
                                </div>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="relative"
                        >
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                                <img 
                                    src="https://images.unsplash.com/photo-1653055645127-54ec96add7b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb25maWRlbnQlMjBwZXJzb24lMjBzbWlsaW5nJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzY3NzUzNTQ1fDA&ixlib=rb-4.1.0&q=85"
                                    alt="Confident person"
                                    className="w-full h-[400px] object-cover"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-primary/40 to-transparent" />
                            </div>
                            
                            {/* Floating Card */}
                            <motion.div 
                                className="absolute -bottom-6 -left-6 bg-card rounded-xl shadow-lg p-4 border border-border"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.5 }}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-full bg-accent/20 flex items-center justify-center">
                                        <TrendingUp className="h-6 w-6 text-accent" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold font-mono text-foreground">+85</p>
                                        <p className="text-sm text-muted-foreground">Avg. Score Increase</p>
                                    </div>
                                </div>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="py-20 px-4 md:px-8 bg-muted/50">
                <div className="max-w-7xl mx-auto">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-16"
                    >
                        <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground mb-4">
                            How CreditFix AI Works
                        </h2>
                        <p className="text-base text-muted-foreground max-w-2xl mx-auto">
                            Our AI-powered platform analyzes your credit reports and generates 
                            legally-sound dispute letters in minutes, not hours.
                        </p>
                    </motion.div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                onMouseEnter={() => setIsHovered(index)}
                                onMouseLeave={() => setIsHovered(null)}
                            >
                                <Card className={`h-full card-hover border ${isHovered === index ? 'border-primary/30' : 'border-border'}`}>
                                    <CardContent className="p-6 space-y-4">
                                        <div className="h-12 w-12 rounded-xl icon-bg flex items-center justify-center">
                                            <feature.icon className="h-6 w-6 text-primary" />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-mono text-primary">0{index + 1}</span>
                                            <h3 className="font-serif text-lg font-semibold text-foreground">
                                                {feature.title}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {feature.description}
                                        </p>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 md:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                        >
                            <img 
                                src="https://images.unsplash.com/photo-1758548157747-285c7012db5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwbW9kZXJuJTIwaG9tZSUyMGludGVyaW9yJTIwYnJpZ2h0fGVufDB8fHx8MTc2Nzc1MzU0N3ww&ixlib=rb-4.1.0&q=85"
                                alt="Modern lifestyle"
                                className="rounded-2xl shadow-lg w-full h-[400px] object-cover"
                            />
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            className="space-y-8"
                        >
                            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-foreground">
                                Everything You Need to
                                <span className="block text-primary">Fix Your Credit</span>
                            </h2>
                            
                            <div className="grid sm:grid-cols-2 gap-4">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <BadgeCheck className="h-5 w-5 text-accent flex-shrink-0" />
                                        <span className="text-foreground">{benefit}</span>
                                    </div>
                                ))}
                            </div>

                            <Card className="bg-primary text-primary-foreground border-0">
                                <CardContent className="p-6 space-y-4">
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="text-sm opacity-80">Full Access</p>
                                            <p className="text-3xl font-bold font-mono">{pricing.one_time_formatted || pricing.formatted}</p>
                                            <p className="text-sm opacity-80">one-time payment</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm opacity-80">or Monthly</p>
                                            <p className="text-3xl font-bold font-mono">{pricing.monthly_formatted || '…'}</p>
                                            <p className="text-sm opacity-80">per month · cancel anytime</p>
                                        </div>
                                    </div>
                                    <Link to="/register" className="block">
                                        <Button 
                                            variant="secondary" 
                                            className="w-full rounded-full btn-press"
                                            data-testid="pricing-get-started-btn"
                                        >
                                            Get Started — Pick Your Plan
                                        </Button>
                                    </Link>
                                </CardContent>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 md:px-8 bg-primary text-primary-foreground">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="space-y-8"
                    >
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 text-white/90">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm font-medium">Results in minutes, not weeks</span>
                        </div>
                        
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold">
                            Ready to Take Control of Your Credit?
                        </h2>
                        
                        <p className="text-lg opacity-90 max-w-2xl mx-auto">
                            Join thousands of people who have improved their credit scores using 
                            our AI-powered credit repair system.
                        </p>
                        
                        <Link to="/register">
                            <Button 
                                size="lg" 
                                variant="secondary"
                                className="rounded-full px-8 text-base btn-press"
                                data-testid="cta-get-started-btn"
                            >
                                Start Your Free Analysis
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 md:px-8 border-t border-border">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="flex items-center gap-2">
                            <Shield className="h-6 w-6 text-primary" />
                            <span className="font-serif text-lg font-semibold">CreditFix AI</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                            © 2024 CreditFix AI. Using FCRA & CFPB guidelines for your protection.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingPage;
